import { useState } from "react";
import { Button, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useInsightMutations } from "@/hooks/useInsightMutations";
import { useAlertMutations } from "@/hooks/useAlertMutations";
import { useAlertValidation } from "@/hooks/useAlertValidation";
import { useScopeParams } from "@/hooks/useScopeParams";
import CreateInsightForm from "./CreateInsightForm";
import CreateAlertForm from "./CreateAlertForm";
import SuccessScreen from "./SuccessScreen";
import { CreateInsightPanelProps, FormDataProps } from "./insights";
import { insightErrorDetail } from "./insights.utils";
import { stripContextPrefix } from "../AskVivo/askVivo.utils";

const MAX_TITLE_LENGTH = 150;
const STEP_TITLE = {
  create: "Create Insight",
  alert: "Add Alert",
  success: "Success!",
} as const;

export default function CreateInsightPanel({
  insight,
  handleClose,
  step,
  hideHeader = false,
  compact = false,
  onStepChange,
}: CreateInsightPanelProps) {
  const { id } = useScopeParams();

  const strippedInput = stripContextPrefix(insight?.input ?? "");
  const defaultTitle = strippedInput
    ? strippedInput.slice(0, MAX_TITLE_LENGTH)
    : "New Insight";

  const [formData, setFormData] = useState<FormDataProps>({
    title: defaultTitle,
    av_question: "",
    av_answer: "",
    category: "uncategorized",
    cadence: "daily",
    is_shared: false,
  });
  const [createdInsightId, setCreatedInsightId] = useState<string | null>(null);
  const [isAlertCreated, setIsAlertCreated] = useState(false);
  const [alertName, setAlertName] = useState("");
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyPush, setNotifyPush] = useState(false);

  const {
    alertRule,
    isAlertRuleValid,
    validateResponse,
    alertEvaluation,
    loadingValidation,
    loadingEvaluation,
    handleRuleChange,
    resetValidation,
    validateAlertRule,
  } = useAlertValidation(id, createdInsightId ?? "");

  const {
    handleCreateInsight: createInsightMutation,
    confirmSubmitLoading: isCreatingInsight,
    isCreateError: hasCreateInsightError,
    createError: createInsightError,
    resetCreateInsight,
  } = useInsightMutations({
    openSuccessNotification: () => {},
    openErrorNotification: () => {},
  });
  const {
    createAlert,
    isCreating: isCreatingAlert,
    isCreateError: hasCreateAlertError,
    resetCreateAlert,
  } = useAlertMutations();

  const resetForm = () => {
    setFormData({
      title: "",
      av_question: "",
      av_answer: "",
      category: "uncategorized",
      cadence: "daily",
      is_shared: false,
    });
  };

  const handleCreateInsight = () => {
    if (formData.title.trim() === "") {
      return;
    }

    // is_shared only goes on the wire when the user actually asked to share.
    // The share checkbox is superuser-only, so posting the default False for
    // everyone else asks the API for a privilege the user does not have.
    const payload = {
      title: formData.title,
      category: formData.category,
      av_question: strippedInput,
      cadence: formData.cadence,
      trial: id,
      askvivo_answer: insight.output,
      selected_sources: insight.selected_sources,
      ...(formData.is_shared ? { is_shared: true } : {}),
    };

    resetCreateInsight();

    createInsightMutation(payload, {
      onSuccess: (newInsight) => {
        onStepChange?.("success");
        setCreatedInsightId(newInsight.id);
        resetForm();
      },
      onError: (error) => {
        console.error("Error creating insight:", error);
        resetForm();
      },
    });
  };

  const handleCreateAlert = () => {
    const payload = {
      insight_id: createdInsightId,
      title: alertName,
      condition: alertRule,
      condition_is_valid: validateResponse?.condition_is_valid,
      condition_is_met: alertEvaluation?.condition_is_met,
      condition_met_detail: alertEvaluation?.condition_met_detail,
      notify_email: notifyEmail,
      notify_push: notifyPush,
    };

    resetCreateAlert();

    createAlert(payload, {
      onSuccess: () => {
        setIsAlertCreated(true);
        onStepChange?.("success");
      },
      onError: (error) => {
        console.error("Error creating alert:", error);
        setIsAlertCreated(false);
      },
    });
  };

  const handleValidateAlertRule = async () => {
    if (alertRule.trim() === "") {
      message.error("Please enter a valid alert rule.");
      return;
    }
    const result = await validateAlertRule();
    if (result?.condition_is_valid) {
      setAlertName(alertRule);
    }
  };

  const onEditRule = () => {
    resetValidation();
    setIsAlertCreated(false);
    resetCreateAlert();
  };

  return (
    <div
      className={
        compact
          ? "flex h-full min-h-0 flex-col overflow-hidden"
          : "flex flex-col mt-[10px] mr-2 ml-3 h-full history-panel"
      }
    >
      {!hideHeader && (
        <div className="flex align-middle items-center justify-between py-2 separator">
          <div className="flex items-center gap-2">
            <p className="text-[14px] text-[#000000d9] font-semibold">
              {STEP_TITLE[step]}
            </p>
          </div>
          <Button
            type="text"
            onClick={handleClose}
            className="text-[#20353E]"
            icon={<CloseOutlined />}
          />
        </div>
      )}

      <div
        className={
          compact ? "flex-1 min-h-0 overflow-y-auto hide-scrollbar" : ""
        }
      >
        {step === "create" && (
          <CreateInsightForm
            formData={formData}
            setFormData={setFormData}
            isLoading={isCreatingInsight}
            errorInsight={hasCreateInsightError}
            errorDetail={insightErrorDetail(createInsightError)}
            onSubmit={handleCreateInsight}
          />
        )}

        {step === "success" && (
          <SuccessScreen
            isAlertCreated={isAlertCreated}
            onAddAlert={() => onStepChange?.("alert")}
            onExit={handleClose}
          />
        )}

        {step === "alert" && (
          <CreateAlertForm
            alertName={alertName}
            isAlertRuleValid={isAlertRuleValid}
            handleValidateAlertRule={handleValidateAlertRule}
            validateResponse={validateResponse}
            alertEvaluation={alertEvaluation}
            loadingValidation={loadingValidation}
            loadingEvaluation={loadingEvaluation}
            loadingCreateAlert={isCreatingAlert}
            errorAlert={hasCreateAlertError}
            handleCreateAlert={handleCreateAlert}
            alertRule={alertRule}
            onRuleChange={handleRuleChange}
            onEditRule={onEditRule}
            onEditAlertName={setAlertName}
            notifyEmail={notifyEmail}
            notifyPush={notifyPush}
            onNotificationChange={(channel, value) => {
              if (channel === "notify_email") setNotifyEmail(value);
              else setNotifyPush(value);
            }}
          />
        )}
      </div>
    </div>
  );
}
